/**
 * 出勤表(その日の割振・時間。2026-09-12。「席」の欄は 2026-09-15 MTG で削除。データの seat は残す)。現行「【日報2609】コールセンター」のシフト表タブと同じ並び。
 *  - 管理者: その日の出勤を作る(席番号・割振・開始/終了・休憩)。上に時間帯ごとの稼働人数が出る
 *  - operator: 自分の行だけを見る
 * 目標取得数 = 稼働 × 2.0件/h(現行シートの値)。「アポ/h」は直近14日の実績(見込取得数/h)。
 * ※「シフト提出」「シフト表(月間)」は ShiftTabs.tsx の別タブ。
 * 2026-09-20 UIデザインシステムへ移行(D-111): 日付の切替は FilterBar、枠は Panel、
 * 表は `.report-scroll` ＋ `.report-table`(割振・スタッフ名を左に固定)、数字は formatters。
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@core/auth/AuthProvider';
import DateField from '@core/components/DateField';
import { IconClose } from '@core/components/icons';
import FilterBar from '@core/components/ui/FilterBar';
import Panel from '@core/components/ui/Panel';
import { formatCount, formatDecimal } from '@core/utils/formatters';
import { useToast } from '@core/components/Toast';
import { useUnsavedGuard } from '@core/components/UnsavedGuard';
import { todayStr, weekdayLabel } from '@core/lib/date';
import { PAY_TYPE_LABELS, WORK_STYLE_LABELS, type Company, type DateStr, type Operator, type PayType, type WorkStyle } from '@core/lib/types';
import { fetchCompanies } from '@core/masters/companyRepo';
import { fetchOperators, primaryStyle } from '@core/masters/operatorRepo';
import { addDays, apphOf, staffMetrics, sumAgg } from '@core/dashboard/calc';
import { fetchMonthTargets, fetchSummariesRange } from '@core/dashboard/dashboardRepo';
import {
  SHIFT_HOURS,
  TARGET_PER_HOUR,
  businessOfBbId,
  boundHoursOf,
  fetchShift,
  inSlot,
  newShiftRowKey,
  saveShift,
  shiftTimeText,
  targetCountOf,
  type ShiftRow,
} from './shiftRepo';
import { breakMinOf, dayHours, fetchSubmissions, gapHoursOf, gapLabel, type ShiftSubmission } from './shiftSubmitRepo';


export default function AttendanceTab() {
  const { user, isAdmin } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();
  const date = (params.get('date') ?? todayStr()) as DateStr;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState<ShiftRow[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [operators, setOperators] = useState<Operator[]>([]);
  const [apph, setApph] = useState<Map<string, number>>(new Map());
  const [dirty, setDirty] = useState(false);
  const [addOp, setAddOp] = useState('');
  const [subs, setSubs] = useState<ShiftSubmission[]>([]);
  const [fromSubmission, setFromSubmission] = useState(false);
  const savedRef = useRef('');
  /** 出勤表を保存した時刻(ミリ秒)。これより後にシフトが変わっていたら知らせる(2026-09-25 発注者指示) */
  const [savedAt, setSavedAt] = useState<number | null>(null);

  const guard = useUnsavedGuard(dirty);
  /** 割振が未定の人数(2026-09-25 発注者指示で色を付ける) */
  const unassigned = useMemo(() => rows.filter((r) => !r.company_id).length, [rows]);
  /**
   * その日の「提出したシフト」の休憩・中抜け・備考(2026-09-25 発注者指示で表に出す)。
   * シフト提出(本人が出したもの)の値で、出勤表の休憩(管理者が入れる)とは別
   */
  const submitted = useMemo(() => {
    const day = date.slice(8, 10);
    const m = new Map<string, { break_min: number; gap_hours: number; gap_label: string; gap_from?: number; gap_to?: number; note: string }>();
    for (const sb of subs) {
      const d = sb.days[day];
      if (!d) continue;
      m.set(sb.op_id, { break_min: breakMinOf(d), gap_hours: gapHoursOf(d), gap_label: gapLabel(d), gap_from: d.gap_from, gap_to: d.gap_to, note: (d.note ?? '').trim() });
    }
    return m;
  }, [subs, date]);

  /**
   * 30分ごとの状態。`on`=稼働 / `gap`=中抜け(シフト提出の時間帯) / ''=シフト外(2026-09-25 発注者指示)
   */
  const slotState = useCallback(
    (r: ShiftRow, slot: number): 'on' | 'gap' | '' => {
      if (!inSlot(r, slot)) return '';
      const sm = submitted.get(r.op_id);
      if (sm?.gap_from !== undefined && sm.gap_to !== undefined && slot >= sm.gap_from && slot + 0.5 <= sm.gap_to) return 'gap';
      return 'on';
    },
    [submitted],
  );

  /**
   * その人のその日の稼働(h)= 拘束 − **シフト提出の休憩・中抜け**(2026-09-25 発注者指示)。
   * 出勤表の休憩の入力はやめ、本人が出した値をそのまま引く
   */
  const workedOf = useCallback(
    (r: ShiftRow) => {
      const sm = submitted.get(r.op_id);
      const minus = sm ? sm.break_min / 60 + sm.gap_hours : r.break_hours || 0;
      return Math.max(0, Math.round((boundHoursOf(r) - minus) * 100) / 100);
    },
    [submitted],
  );

  /** 追加できるスタッフ(在籍中で、まだ表に居ない人) */
  const addable = useMemo(() => operators.filter((o) => o.status === 'active' && !rows.some((r) => r.op_id === o.id)), [operators, rows]);
  /**
   * 出勤表を保存した後にシフトが変わった人(2026-09-25 発注者指示)。
   * シフト提出の変更の記録(logs)が保存時刻より新しいかで見る
   */
  const changedAfterSave = useMemo(() => {
    if (!savedAt) return [] as string[];
    const day = date.slice(8, 10);
    return subs.filter((s) => (s.logs ?? []).some((l) => l.day === day && Date.parse(l.at) > savedAt)).map((s) => s.name);
  }, [subs, savedAt, date]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const from = addDays(date, -14) as DateStr;
      const [shift, cs, ops, sums, tg, sb] = await Promise.all([
        fetchShift(date),
        fetchCompanies(),
        fetchOperators(),
        fetchSummariesRange(from, date),
        fetchMonthTargets(date.slice(0, 7)),
        fetchSubmissions(date.slice(0, 7)),
      ]);
      setSubs(sb);
      setCompanies(cs);
      setOperators(ops);
      const map = new Map(cs.map((c) => [c.id, c]));
      const metrics = sums.flatMap((s) => s.staff.map((st) => staffMetrics(st, s, tg, map)));
      const byOp = new Map<string, number>();
      for (const op of new Set(metrics.map((m) => m.op_id))) {
        const v = apphOf(sumAgg(metrics.filter((m) => m.op_id === op)));
        if (v !== null) byOp.set(op, v);
      }
      setApph(byOp);
      const ts = shift?.updated_at as { seconds?: number; toMillis?: () => number } | undefined;
      setSavedAt(ts?.toMillis?.() ?? (typeof ts?.seconds === 'number' ? ts.seconds * 1000 : null));
      const saved = shift?.rows ?? [];
      if (saved.length > 0) {
        setRows(saved);
        savedRef.current = JSON.stringify(saved);
        setDirty(false);
        setFromSubmission(false);
      } else {
        // まだ作っていない日は、その日のシフト提出から自動で並べる(保存するまでは仮)
        const day = date.slice(8, 10);
        const auto = sb.flatMap((s) => {
          const d = s.days[day];
          if (!d) return [];
          const op = ops.find((o) => o.id === s.op_id);
          const acc = op?.bb_accounts.find((a) => a.bb_id === s.bb_id) ?? op?.bb_accounts[0];
          return [
            {
              key: newShiftRowKey(),
              op_id: s.op_id,
              bb_id: s.bb_id || (acc?.bb_id ?? ''),
              name: s.name || (op?.name ?? ''),
              work_style: acc?.work_style ?? null,
              company_id: null,
              seat: '',
              start: d.start,
              end: d.end,
              break_hours: 0,
              submitted_hours: dayHours(d),
              note: '',
            } as ShiftRow,
          ];
        });
        setRows(auto);
        // 自動で並べただけでは「変更あり」にしない(何も触っていないのに離脱確認が出るのを防ぐ)
        savedRef.current = JSON.stringify(auto);
        setDirty(false);
        setFromSubmission(auto.length > 0);
      }
    } catch (e) {
      toast(e instanceof Error ? e.message : '読み込みに失敗しました');
    } finally {
      setLoading(false);
    }
  }, [date, toast]);

  useEffect(() => {
    void load();
  }, [load]);

  const patch = (key: string, p: Partial<ShiftRow>) => {
    setRows((prev) => {
      const next = prev.map((r) => (r.key === key ? { ...r, ...p } : r));
      setDirty(JSON.stringify(next) !== savedRef.current);
      return next;
    });
  };

  const addRow = (opId: string) => {
    const op = operators.find((o) => o.id === opId);
    if (!op) return;
    const st = primaryStyle(op.bb_accounts);
    const row: ShiftRow = {
      key: newShiftRowKey(),
      op_id: op.id,
      bb_id: op.bb_accounts[0]?.bb_id ?? '',
      name: op.name,
      work_style: st.work_style,
      company_id: null,
      seat: '',
      start: 9,
      end: 18,
      break_hours: 0,
      submitted_hours: null,
      note: '',
    };
    setRows((prev) => {
      const next = [...prev, row];
      setDirty(true);
      return next;
    });
    setAddOp('');
  };

  const removeRow = (key: string) => {
    setRows((prev) => {
      const next = prev.filter((r) => r.key !== key);
      setDirty(JSON.stringify(next) !== savedRef.current);
      return next;
    });
  };

  /** 出勤形態×報酬区分(現行シートの並び順) */
  const STYLE_ORDER: { key: string; ws: WorkStyle; pt: PayType }[] = [
    { key: 'shinagawa_hourly', ws: 'shinagawa', pt: 'hourly' },
    { key: 'timee_hourly', ws: 'timee', pt: 'hourly' },
    { key: 'zaitaku_commission', ws: 'zaitaku', pt: 'commission' },
    { key: 'zaitaku_hourly', ws: 'zaitaku', pt: 'hourly' },
    { key: 'kanri_hourly', ws: 'kanri', pt: 'hourly' },
  ];
  const styleOf = useCallback(
    (r: ShiftRow) => {
      const op = operators.find((o) => o.id === r.op_id);
      const acc = op?.bb_accounts.find((a) => a.bb_id === r.bb_id) ?? op?.bb_accounts[0];
      const ws = acc?.work_style ?? r.work_style ?? 'zaitaku';
      const pt = acc?.pay_type ?? 'hourly';
      return { key: `${ws}_${pt}`, label: `${WORK_STYLE_LABELS[ws]}／${PAY_TYPE_LABELS[pt]}` };
    },
    [operators],
  );
  const visibleRows = useMemo(() => {
    const list = isAdmin ? rows : rows.filter((r) => r.op_id === user?.op_id);
    const idx = (r: ShiftRow) => {
      const i = STYLE_ORDER.findIndex((s) => s.key === styleOf(r).key);
      return i < 0 ? STYLE_ORDER.length : i;
    };
    return [...list].sort((a, b) => idx(a) - idx(b) || (a.seat || 'zzz').localeCompare(b.seat || 'zzz') || a.name.localeCompare(b.name, 'ja'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, isAdmin, user?.op_id, styleOf]);

  /** その日のシフト提出から出勤者を入れる(現行フロー: シフト表から翌日の出勤者をコピペ) */
  const importFromSubmissions = () => {
    const day = date.slice(8, 10);
    const add: ShiftRow[] = [];
    for (const s of subs) {
      const d = s.days[day];
      if (!d) continue;
      if (rows.some((r) => r.op_id === s.op_id)) continue;
      const op = operators.find((o) => o.id === s.op_id);
      const acc = op?.bb_accounts.find((a) => a.bb_id === s.bb_id) ?? op?.bb_accounts[0];
      add.push({
        key: newShiftRowKey(),
        op_id: s.op_id,
        bb_id: s.bb_id || (acc?.bb_id ?? ''),
        name: s.name || (op?.name ?? ''),
        work_style: acc?.work_style ?? null,
        company_id: null,
        seat: '',
        start: d.start,
        end: d.end,
        break_hours: 0,
        submitted_hours: dayHours(d),
        note: '',
      });
    }
    if (add.length === 0) {
      toast('この日のシフト提出はありません');
      return;
    }
    setRows((prev) => {
      const next = [...prev, ...add];
      setDirty(true);
      return next;
    });
    toast(`シフト提出から ${add.length}名を入れました`);
  };
  /** 時間帯ごとの人数(中抜けの人は数えない。2026-09-25 発注者指示) */
  const head = useMemo(
    () => SHIFT_HOURS.map((h) => rows.filter((r) => slotState(r, h) === 'on' || slotState(r, h + 0.5) === 'on').length),
    [rows, slotState],
  );
  const totals = useMemo(() => {
    const worked = rows.reduce((s, r) => s + workedOf(r), 0);
    // 目標は行ごとに架電内容で係数が違う(給湯器 2.0件/h・不用品 3時間1件)ので、行の目標を足す
    const target = rows.reduce((s, r) => s + targetCountOf(r, businessOfBbId(operators, r.bb_id)), 0);
    return { worked, target: Math.round(target * 10) / 10, people: rows.length };
  }, [rows, operators, workedOf]);

  const save = async () => {
    if (!user) return;
    setSaving(true);
    try {
      await saveShift(date, rows, user.id);
      savedRef.current = JSON.stringify(rows);
      setDirty(false);
      setFromSubmission(false);
      toast('出勤表を保存しました');
    } catch (e) {
      toast(e instanceof Error ? e.message : '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  const setDate = (d: DateStr) => {
    const next = new URLSearchParams(params);
    next.set('date', d);
    setParams(next, { replace: true });
  };

  if (loading) {
    return <div className="ui-empty">読み込み中…</div>;
  }

  return (
    <>
      {/* 未保存のまま離れるときの確認。これを描いていないと遷移が止まったままになる(2026-09-12 修正) */}
      {guard.modal}

      <FilterBar label="表示する日">
        <DateField value={date} onChange={setDate} />
        <span className="ui-period-label">
          {date.replaceAll('-', '/')}
          {weekdayLabel(date)}
        </span>
      </FilterBar>

      {changedAfterSave.length > 0 && (
        <div className="ui-panel" style={{ borderLeft: '4px solid var(--color-warning)' }}>
          <p style={{ margin: 0 }}>
            <b>保存した後にシフトが変わっています</b>({formatCount(changedAfterSave.length, { unit: '名' })}): {changedAfterSave.join('・')}
            {isAdmin && (
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" style={{ marginLeft: 8 }} onClick={importFromSubmissions}>
                シフト提出から入れ直す
              </button>
            )}
          </p>
        </div>
      )}

      <Panel
        title="出勤表"
        meta={`${formatCount(totals.people, { unit: '名' })} ／ 稼働 ${formatDecimal(totals.worked, { digits: 1 })}h ／ 目標 ${formatCount(Math.round(totals.target), { unit: '件' })}${unassigned > 0 ? ` ／ 割振 未定 ${formatCount(unassigned, { unit: '名' })}` : ''}`}
        actions={
          isAdmin ? (
            <>
              <button type="button" className="ui-btn ui-btn-secondary ui-btn-sm" onClick={importFromSubmissions}>
                シフト提出から入れる
              </button>
              <button type="button" className="ui-btn ui-btn-primary ui-btn-sm" disabled={(!dirty && !fromSubmission) || saving} onClick={() => void save()}>
                {saving ? '保存中…' : '保存'}
              </button>
            </>
          ) : undefined
        }
        note={fromSubmission ? 'この日の出勤表はまだ保存されていません。**シフト提出から自動で並べています**。割振を入れて「保存」してください' : undefined}
      >
        {rows.length === 0 ? (
          <div className="ui-empty">この日はシフト提出がありません{isAdmin ? '。下の「スタッフを追加」から手で足せます' : ''}</div>
        ) : (
          <div className="attendance-wrap">
            <table className="report-table no-stack shift-table">
              <thead>
                <tr>
                  <th scope="col" className="is-sticky" style={{ left: 0, width: 110, minWidth: 110 }}>割振</th>
                  <th scope="col" className="is-sticky is-sticky-edge" style={{ left: 110, width: 120, minWidth: 120 }}>スタッフ名</th>
                  <th scope="col" className="is-num" style={{ width: 56 }}>アポ/h</th>
                  <th scope="col" style={{ width: 76 }}>業務形態</th>
                  <th scope="col" style={{ width: 72 }}>BB ID</th>
                  <th scope="col" style={{ width: 84 }}>稼働時間</th>
                  <th scope="col" style={{ width: 110 }}>休憩・中抜け(提出)</th>
                  {SHIFT_HOURS.map((h) => (
                    <th key={h} scope="col" className="slot">{h}</th>
                  ))}
                  <th scope="col" className="is-num" style={{ width: 48 }}>拘束</th>
                  <th scope="col" className="is-num" style={{ width: 48 }}>稼働</th>
                  <th scope="col" className="is-num" style={{ width: 56 }}>目標</th>
                  {isAdmin && <th scope="col" style={{ width: 34 }} />}
                </tr>
              </thead>
              <tbody>
                <tr className="shift-head">
                  <th colSpan={7}>時間帯ごとの人数</th>
                  {head.map((n, i) => (
                    <td key={i} className="slot">{n || ''}</td>
                  ))}
                  <td colSpan={isAdmin ? 5 : 4} />
                </tr>
                {visibleRows.map((r, i) => (
                  <Fragment key={r.key}>
                    {(i === 0 || styleOf(visibleRows[i - 1]!).key !== styleOf(r).key) && (
                      <tr className="shift-group">
                        <th colSpan={isAdmin ? 21 : 20}>{styleOf(r).label}</th>
                      </tr>
                    )}
                    <tr className={r.company_id ? undefined : 'no-company'}>
                      <td className="is-sticky" style={{ left: 0 }}>
                        {isAdmin ? (
                          <select className="ui-input is-left" value={r.company_id ?? ''} onChange={(e) => patch(r.key, { company_id: e.target.value || null })} style={{ width: '100%' }} aria-label={`${r.name} の割振`}>
                            <option value="">未定</option>
                            {companies.filter((c) => c.status === 'active').map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </select>
                        ) : (
                          (companies.find((c) => c.id === r.company_id)?.name ?? '未定')
                        )}
                      </td>
                      <th scope="row" className="is-sticky is-sticky-edge" style={{ left: 110 }}>{r.name}</th>
                      <td className="is-num">{formatDecimal(apph.get(r.op_id) ?? null, { digits: 1 })}</td>
                      <td className="ui-hint">{r.work_style ? WORK_STYLE_LABELS[r.work_style] : ''}</td>
                      <td className="ui-hint">{r.bb_id}</td>
                      {/* 時間はシフト(提出)のもの。ここでは直さない(2026-09-25 発注者指示。直すのはシフト表から) */}
                      <td className="shift-time">{shiftTimeText(r)}</td>
                      {(() => {
                        const sm = submitted.get(r.op_id);
                        const parts = sm ? [sm.break_min > 0 ? `休憩${sm.break_min}分` : '', sm.gap_hours > 0 ? `中抜け${sm.gap_label}` : ''].filter(Boolean) : [];
                        return (
                          <td className="submitted-rest" title={sm?.note || undefined}>
                            {parts.length === 0 ? <span className="cell-na">—</span> : parts.map((t, i) => (t.startsWith('中抜け') ? <span key={i} className="gap">{i > 0 ? '・' : ''}{t}</span> : <span key={i}>{t}</span>))}
                            {sm?.note ? <span className="ui-hint"> ※</span> : null}
                          </td>
                        );
                      })()}
                      {SHIFT_HOURS.map((h) => (
                        // 30分ずつの2つに分けて塗る(稼働=青 / 中抜け=斜線)
                        <td key={h} className="slot">
                          <span className={`half ${slotState(r, h)}`} />
                          <span className={`half ${slotState(r, h + 0.5)}`} />
                        </td>
                      ))}
                      <td className="is-num">{formatDecimal(boundHoursOf(r), { digits: 1 })}</td>
                      {/* 稼働 = 拘束 − シフト提出の休憩・中抜け(2026-09-25 発注者指示) */}
                      <td className="is-num">{formatDecimal(workedOf(r), { digits: 1 })}</td>
                      <td className="is-num">{formatDecimal(targetCountOf(r, businessOfBbId(operators, r.bb_id)), { digits: 1 })}</td>
                      {isAdmin && (
                        <td className="is-num">
                          <button type="button" className="icon-btn" title="この行を削除" onClick={() => removeRow(r.key)}>
                            <IconClose size={12} />
                          </button>
                        </td>
                      )}
                    </tr>
                  </Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {isAdmin && (
          <div className="report-toolbar" style={{ marginTop: 'var(--space-3)' }}>
            {/* 名前を打つと候補が出る(人数が多いので選ぶのが大変。2026-09-25 発注者指示) */}
            <input
              className="ui-input"
              aria-label="スタッフを追加"
              list="attendance-add-op"
              placeholder="スタッフ名を入力して追加…"
              value={addOp}
              onChange={(e) => {
                const v = e.target.value;
                setAddOp(v);
                const hit = addable.find((o) => `${o.name}(${o.op_code})` === v || o.name === v || o.op_code === v);
                if (hit) {
                  addRow(hit.id);
                  setAddOp('');
                }
              }}
              style={{ minWidth: 260 }}
            />
            <datalist id="attendance-add-op">
              {addable.map((o) => (
                <option key={o.id} value={`${o.name}(${o.op_code})`} />
              ))}
            </datalist>
            <span className="ui-hint">目標取得数は 稼働 × {TARGET_PER_HOUR}件/h(不用品は3時間で1件)で計算します</span>
          </div>
        )}
      </Panel>
    </>
  );
}
